import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import styles from "./PhonePeDrawer.module.css";
import Api from "../../utils/Api/Api";
import Helper from "../../utils/Helper";
import { money } from "../../utils/format";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";
import QRCode from "qrcode";
import upiLogo from "../../asset/upi/upi-mark.svg";
import phonepeLogo from "../../asset/upi/phonepe-symbol.svg";
import gpayLogo from "../../asset/upi/google-pay-icon.png";
import paytmLogo from "../../asset/upi/paytm-icon.jpeg";
import credLogo from "../../asset/upi/cred-icon.jpeg";

const SUPPORT_URL = "https://wa.me/919148477518";

/* Polling shape. The backoff curve is the one the drawer has always used; the
   cap was 6 s, which left a buyer coming back from the UPI app staring at the
   sheet for up to 6 s after paying. At 3 s the sheet needs more attempts for
   the same patience: the old 30 attempts gave up about 162 s in and called
   it slow at about 103 s; 55 and 35 keep both within a second of that. */
const FIRST_POLL_MS = 800;
const BACKOFF_BASE_MS = 2000;
const BACKOFF_FACTOR = 1.2;
export const BACKOFF_CAP_MS = 3000;
const SLOW_AFTER_ATTEMPTS = 35;
export const MAX_ATTEMPTS = 55;
/* Coming back to the page polls at once. A request already in flight that
   started this recently is as good as a new one, so the visibilitychange and
   focus that arrive together on return don't poll twice. */
const FRESH_REQUEST_MS = 1000;
const MAX_CONSECUTIVE_ERRORS = 10;
const SUCCESS_HOLD_MS = 800;
const COUNTDOWN_SECONDS = 5 * 60;

/* Brand marks for the app tiles. The monogram is the fallback drawn if a
   logo file fails to load, so a tile never renders as a broken image. */
const APP_MARKS = {
  UPI: { logo: upiLogo, fit: "contain", pad: 6, mono: "UPI", tint: "#0e7a82" },
  PhonePe: { logo: phonepeLogo, fit: "cover", pad: 0, mono: "Pe", tint: "#5f259f" },
  GPay: { logo: gpayLogo, fit: "cover", pad: 0, mono: "G", tint: "#1a73e8" },
  Paytm: { logo: paytmLogo, fit: "cover", pad: 0, mono: "Pt", tint: "#00baf2" },
  CRED: { logo: credLogo, fit: "cover", pad: 0, mono: "C", tint: "#0f0f0f" },
};

const IS_IOS =
  typeof navigator !== "undefined" &&
  (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

/* A UPI intent is one URL with a query string; each app claims its own scheme
   and reads the same parameters. Google Pay registers "tez" on Android and
   "gpay" on iOS. */
const APP_SCHEMES = {
  PhonePe: "phonepe://pay",
  GPay: IS_IOS ? "gpay://upi/pay" : "tez://upi/pay",
  Paytm: "paytmmp://pay",
  CRED: "credpay://upi/pay",
};

/* The generic "UPI" tile opens upi://pay. Android answers with the system
   chooser listing every UPI app, which is what the design intends. iOS has no
   chooser: it hands the link to one arbitrary app that claimed the scheme
   (a bank app, Cheq, whatever was installed last), so on iOS the tile is
   replaced by a fourth named app. */
const TILE_ORDER = IS_IOS
  ? ["PhonePe", "GPay", "Paytm", "CRED"]
  : ["UPI", "PhonePe", "GPay", "Paytm"];

/* If the phone never leaves the browser this long after a tile tap, the app
   is not installed: iOS fails silently, Android shows a dead-end page. */
const APP_OPEN_GRACE_MS = 1800;

/* Card / netbanking / wallet rows from the prototype. The website has no
   backend for them yet, so they render disabled (decision: show designed
   elements everywhere, disabled where non-functional). */
const OTHER_METHODS = [
  { name: "Credit or debit card", meta: "Visa, Mastercard, RuPay", mono: "CD" },
  { name: "Netbanking", meta: "All major banks", mono: "NB" },
  { name: "Wallets", meta: "Amazon Pay, Mobikwik", mono: "W" },
];

const toAppIntent = (baseUrl, appName) => {
  const scheme = APP_SCHEMES[appName];
  if (!baseUrl || !scheme || !baseUrl.startsWith("upi://")) return baseUrl;
  try {
    return `${scheme}${new URL(baseUrl).search}`;
  } catch {
    return baseUrl;
  }
};

const clock = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/**
 * UPI pay sheet, rebuilt from design/Purchase Flow.dc.html (payOpen block).
 *
 * Written from scratch - markup, styles and the polling state machine. The
 * success condition (`paid` AND `is_purchase_done`) and the polling cadence are
 * carried forward as values because they are tuned, not because the old code
 * was copied.
 *
 * Shown only when the backend routes payment to PhonePe. A `payment_url`
 * response redirects to the gateway's own hosted page instead, which this
 * component never sees.
 */
export const PhonePeDrawer = ({
  visible,
  onClose,
  orderId,
  paymentOptions: paymentOptionsProp,
  paymentId,
  onSuccess,
  autoOpenOnMobile = false,
}) => {
  const isMobile = Helper.isMobile();

  const [options, setOptions] = useState(paymentOptionsProp || null);
  const [amount, setAmount] = useState(null);
  const [phase, setPhase] = useState("choosing"); // choosing | handoff | success | failed
  const [isSlow, setIsSlow] = useState(false);
  const [handoffApp, setHandoffApp] = useState(null);
  const [missingApp, setMissingApp] = useState(null);
  const [brokenLogos, setBrokenLogos] = useState({});
  const leftPageRef = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);

  // Callbacks live in refs so the polling effect never restarts because a
  // parent re-rendered and handed us a new closure.
  const onSuccessRef = useRef(onSuccess);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onCloseRef.current = onClose;
  });

  const autoOpenedRef = useRef(false);

  /* ---- Open / close ---- */

  useEffect(() => {
    if (!visible) return;
    setPhase("choosing");
    setIsSlow(false);
    setHandoffApp(null);
    setSecondsLeft(COUNTDOWN_SECONDS);
    autoOpenedRef.current = false;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible, paymentId]);

  useEffect(() => {
    if (paymentOptionsProp) setOptions(paymentOptionsProp);
  }, [paymentOptionsProp]);

  /* ---- Amount, and the case where payment already completed ---- */

  useEffect(() => {
    if (!visible || !orderId) return;
    let cancelled = false;

    Api.ProcessPaymentV4({ order_id: orderId, payment_id: paymentId })
      .then(({ data }) => {
        if (cancelled || !data) return;

        const settled = data.final_amount_in_inr ?? data.amount;
        if (settled != null) setAmount(settled);
        if (data.payment_options) setOptions((prev) => prev || data.payment_options);

        // Re-opening after a completed payment (a refresh, a restored tab)
        // must not present a fresh QR for an order that is already paid.
        if (String(data.status || "").toLowerCase() === "paid" && data.is_purchase_done) {
          setPhase("success");
          setTimeout(() => onSuccessRef.current?.(), 1500);
        }
      })
      .catch(() => {
        /* The poller below reports failures; one lost prefetch is not worth
           surfacing on its own. */
      });

    return () => {
      cancelled = true;
    };
  }, [visible, orderId, paymentId]);

  /* ---- Status polling ---- */

  useEffect(() => {
    if (!visible || !orderId) return;

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    let consecutiveErrors = 0;
    let inFlight = false;
    let startedAt = 0;
    let pollOnSettle = false;

    const stop = () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };

    // The next poll, unless the buyer came back while a request was out:
    // then straight away, since that request may predate the payment.
    const schedule = (delay) => {
      const wait = pollOnSettle ? 0 : delay;
      pollOnSettle = false;
      timer = setTimeout(tick, wait);
    };

    const tick = () => {
      if (cancelled || inFlight) return;
      if (timer) clearTimeout(timer);
      timer = null;
      inFlight = true;
      startedAt = Date.now();

      Api.ProcessPaymentV4({ order_id: orderId, payment_id: paymentId })
        .finally(() => {
          inFlight = false;
        })
        .then(({ data }) => {
          if (cancelled || !data) return;
          consecutiveErrors = 0;

          if (data.amount != null) setAmount(data.amount);
          const status = String(data.status || "").toLowerCase();

          if (status === "paid" && data.is_purchase_done) {
            stop();
            setPhase("success");
            setTimeout(() => onSuccessRef.current?.(), SUCCESS_HOLD_MS);
            return;
          }

          if (status === "failed" || status === "fail") {
            stop();
            setPhase("failed");
            return;
          }

          attempts += 1;
          if (attempts >= SLOW_AFTER_ATTEMPTS) setIsSlow(true);
          if (attempts >= MAX_ATTEMPTS) {
            stop();
            return;
          }

          schedule(Math.min(BACKOFF_BASE_MS * BACKOFF_FACTOR ** attempts, BACKOFF_CAP_MS));
        })
        .catch((err) => {
          if (cancelled) return;
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.warn("UPI status polling gave up:", err?.message || err);
            stop();
            setIsSlow(true);
            return;
          }
          schedule(BACKOFF_BASE_MS);
        });
    };

    // Back from the UPI app (or another tab): ask now rather than wait out
    // the backoff. Counts as an ordinary attempt; changes nothing else.
    const onReturn = () => {
      if (cancelled || document.visibilityState === "hidden") return;
      if (inFlight) {
        if (Date.now() - startedAt > FRESH_REQUEST_MS) pollOnSettle = true;
        return;
      }
      tick();
    };

    timer = setTimeout(tick, FIRST_POLL_MS);
    document.addEventListener("visibilitychange", onReturn);
    window.addEventListener("focus", onReturn);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onReturn);
      window.removeEventListener("focus", onReturn);
    };
  }, [visible, orderId, paymentId]);

  /* ---- Countdown ---- */

  useEffect(() => {
    if (!visible || secondsLeft <= 0) return;
    const t = setTimeout(() => setSecondsLeft((n) => Math.max(n - 1, 0)), 1000);
    return () => clearTimeout(t);
  }, [visible, secondsLeft]);

  /* ---- Derived payment options ---- */

  const gatewayQr = useMemo(() => {
    const raw =
      options?.qr_data || options?.upi_qr_base64 || options?.qrImage || null;
    if (typeof raw !== "string" || !raw) return null;
    // The gateway sends bare base64; a data: or http URL is already usable.
    return /^(data:|https?:)/.test(raw) ? raw : `data:image/png;base64,${raw}`;
  }, [options]);

  const baseIntent =
    options?.intent_url ||
    options?.upi_intent_url ||
    options?.intent?.upi ||
    options?.upi_intent ||
    null;

  // A UPI QR is nothing more than the intent URL encoded, so whenever the
  // gateway gives us that URL (it always does, in both UPI_QR and UPI_INTENT
  // modes) the QR is drawn here at error-correction level H. That is what
  // lets the design's Fleek mark sit on it without breaking scans. The
  // gateway's own image is the fallback when no intent URL comes back.
  const [localQr, setLocalQr] = useState(null);
  useEffect(() => {
    if (!baseIntent) {
      setLocalQr(null);
      return undefined;
    }
    let cancelled = false;
    QRCode.toDataURL(baseIntent, {
      margin: 0,
      width: 512,
      errorCorrectionLevel: "H",
      color: { dark: "#000000", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setLocalQr(url);
      })
      .catch(() => {
        if (!cancelled) setLocalQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [baseIntent]);

  const qrSrc = localQr || gatewayQr;
  const qrHasMark = !!localQr;

  // Four tiles, because the design's grid is four wide and a fifth strands
  // itself on a row of its own. See TILE_ORDER for the iOS/Android split.
  const tiles = useMemo(() => {
    if (!baseIntent) return [];
    return TILE_ORDER.map((name) => ({
      name,
      href: name === "UPI" ? baseIntent : toAppIntent(baseIntent, name),
    }));
  }, [baseIntent]);

  /* ---- Actions ---- */

  const openApp = useCallback((tile) => {
    leftPageRef.current = false;
    setMissingApp(null);
    setHandoffApp(tile.name);
    setPhase("handoff");
  }, []);

  const backToApps = useCallback(() => {
    setHandoffApp(null);
    setPhase("choosing");
  }, []);

  /* ---- App-not-installed detection ---- */

  useEffect(() => {
    if (phase !== "handoff") return undefined;
    const leave = () => {
      leftPageRef.current = true;
    };
    document.addEventListener("visibilitychange", leave);
    window.addEventListener("pagehide", leave);
    window.addEventListener("blur", leave);
    const t = setTimeout(() => {
      if (leftPageRef.current || document.hidden) return;
      setMissingApp(handoffApp);
      setHandoffApp(null);
      setPhase("choosing");
    }, APP_OPEN_GRACE_MS);
    return () => {
      clearTimeout(t);
      document.removeEventListener("visibilitychange", leave);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("blur", leave);
    };
  }, [phase, handoffApp]);

  const close = useCallback(() => {
    // Best effort: releases the gateway's hold on the order. The sheet closes
    // either way, so a failed cancel never traps the customer here.
    if (paymentId) Api.CancelPayment({ payment_id: paymentId }).catch(() => {});
    onCloseRef.current?.();
  }, [paymentId]);

  /* ---- Auto-handoff (opt-in) ---- */

  useEffect(() => {
    if (!autoOpenOnMobile || !visible || !isMobile) return;
    if (!baseIntent || autoOpenedRef.current) return;
    autoOpenedRef.current = true;
    const t = setTimeout(() => {
      window.location.href = baseIntent;
    }, 400);
    return () => clearTimeout(t);
  }, [autoOpenOnMobile, visible, isMobile, baseIntent]);

  if (!visible) return null;

  const isFailed = phase === "failed";
  const isSuccess = phase === "success";
  const showTiles = tiles.length > 0;

  return (
    <div
      className={`${styles.overlay} ${isMobile ? "" : styles.overlayCentred}`}
      role="dialog"
      aria-modal="true"
      aria-label="Pay with UPI"
    >
      <div className={`${styles.sheet} ${isMobile ? "" : styles.sheetCentred}`}>
        {isMobile && <div className={styles.grabber} aria-hidden="true" />}

        <div className={styles.header}>
          <h2 className={styles.title}>{isFailed ? "Retry payment" : "Pay with UPI"}</h2>
          {amount != null && <span className={styles.amount}>{money(amount)}</span>}
        </div>

        {isSuccess ? (
          <div className={styles.terminal}>
            <span className={styles.terminalIcon} aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            <p className={styles.terminalTitle}>Payment successful</p>
            <p className={styles.terminalBody}>Taking you to your order…</p>
          </div>
        ) : (
          <>
            {isFailed ? (
              <>
                <div className={styles.declined} role="alert">
                  <span className={styles.declinedIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M15 9l-6 6" />
                      <path d="M9 9l6 6" />
                    </svg>
                  </span>
                  <div>
                    <div className={styles.declinedTitle}>Payment didn't go through</div>
                    <p className={styles.declinedBody}>
                      Your bank declined the request. Nothing was charged — if you see a
                      debit it'll reverse within 3 working days.
                    </p>
                  </div>
                </div>
                {showTiles && <p className={styles.retryPrompt}>Select your UPI app to retry</p>}
              </>
            ) : phase === "handoff" ? (
              <div className={styles.handoff}>
                <div className={styles.spinner} />
                <p className={styles.handoffTitle}>
                  Finish paying in {handoffApp}
                </p>
                <p className={styles.handoffBody}>
                  Come back to this tab once you're done — we'll confirm it here
                  automatically.
                </p>
                <div className={styles.actions}>
                  <a
                    className={`${styles.action} ${styles.actionPrimary}`}
                    href={tiles.find((t) => t.name === handoffApp)?.href || baseIntent}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                  >
                    Open {handoffApp} again
                  </a>
                  <button type="button" className={`${styles.action} ${styles.actionGhost}`} onClick={backToApps}>
                    Use another app
                  </button>
                </div>
              </div>
            ) : (
              <p className={styles.lede}>Scan &amp; pay via any UPI app</p>
            )}

            {phase !== "handoff" && (
              <>
                <div className={styles.qrWrap}>
                  <div className={`${styles.qrFrame} ${isMobile ? "" : styles.qrFrameLarge}`}>
                    {qrSrc ? (
                      <>
                        <img className={styles.qrImage} src={qrSrc} alt="UPI QR code" />
                        {qrHasMark && (
                          <span className={styles.qrLogo} aria-hidden="true">
                            <img className={styles.qrLogoImg} src={FleekIcon} alt="" />
                          </span>
                        )}
                      </>
                    ) : (
                      <span className={styles.qrPending}>
                        <span className={styles.spinner} />
                        Generating QR…
                      </span>
                    )}
                  </div>
                </div>

                {showTiles && (
                  <>
                    <div className={styles.divider}>
                      <span className={styles.dividerRule} />
                      <span className={styles.dividerLabel}>or pay with</span>
                      <span className={styles.dividerRule} />
                    </div>

                    {missingApp && (
                      <p className={styles.notice} role="status">
                        Couldn't open {missingApp} — it doesn't look installed on this
                        phone. Try another app or scan the QR.
                      </p>
                    )}

                    <div className={styles.tiles}>
                      {tiles.map((tile) => {
                        const mark = APP_MARKS[tile.name];
                        return (
                          <a
                            key={tile.name}
                            className={styles.tile}
                            href={tile.href}
                            onClick={() => openApp(tile)}
                            aria-label={`Pay in ${tile.name}`}
                          >
                            {mark.logo && !brokenLogos[tile.name] ? (
                              <span className={styles.tileMark} aria-hidden="true">
                                <img
                                  className={styles.tileLogo}
                                  style={{ objectFit: mark.fit, padding: mark.pad }}
                                  src={mark.logo}
                                  alt=""
                                  onError={() =>
                                    setBrokenLogos((b) => ({ ...b, [tile.name]: true }))
                                  }
                                />
                              </span>
                            ) : (
                              <span
                                className={`${styles.tileMark} ${styles.tileMono}`}
                                style={{ background: mark.tint }}
                                aria-hidden="true"
                              >
                                {mark.mono}
                              </span>
                            )}
                            <span className={styles.tileName}>{tile.name}</span>
                          </a>
                        );
                      })}
                    </div>

                    <div className={styles.methods}>
                      {OTHER_METHODS.map((m) => (
                        <button key={m.name} type="button" className={styles.method} disabled aria-disabled="true" title="Coming soon">
                          <span className={styles.methodMono} aria-hidden="true">{m.mono}</span>
                          <span className={styles.methodText}>
                            <span className={styles.methodName}>{m.name}</span>
                            <span className={styles.methodMeta}>{m.meta}</span>
                          </span>
                          <span className={styles.methodChevron} aria-hidden="true">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}

            {isSlow && !isFailed && (
              <div className={styles.slow} role="status">
                <span className={styles.slowIcon}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v4" />
                    <path d="M12 16h.01" />
                  </svg>
                </span>
                <span className={styles.slowText}>
                  This is taking longer than usual. If you've already paid, don't pay
                  again — <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">message us</a> and
                  we'll sort it out.
                </span>
              </div>
            )}

            {!isFailed && phase !== "handoff" && secondsLeft > 0 && (
              <p className={styles.footNote}>
                This QR expires in {clock(secondsLeft)}
              </p>
            )}

            <button type="button" className={styles.cancel} onClick={close}>
              Cancel payment
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PhonePeDrawer;
